import type { MaybeRefOrGetter, Ref } from 'vue'
import type { PendingPublish, PendingPublishGroupKey } from '~/composables/useOnlineStores'

const EMPTY: PendingPublish = {
  has_pending: false,
  count: 0,
  groups: [],
  published: false,
  last_published_at: null,
}

export const STORE_DRAFTS_PER_PAGE = 10

export type StoreDraft = {
  id: number
  action: string
  group: string
  entity: string
  entity_id: number
  label: string | null
  changed_fields: string[]
  changes: string[]
  created_at: string | null
}

export type StoreDraftPage = {
  drafts: StoreDraft[]
  nextCursor: string | null
}

export type PendingPublishCache = Ref<Record<string, PendingPublish>>

/**
 * Shared draft state, keyed by store id.
 *
 * Resolve this where a Nuxt instance is guaranteed (a composable body or
 * `setup`), then hand the ref to the mutators below — they run after a fetch
 * resolves, where `useState` is no longer available.
 */
export function usePendingPublishCache(): PendingPublishCache {
  return useState<Record<string, PendingPublish>>('store-pending-publish', () => ({}))
}

export function pendingPublishFor(cache: PendingPublishCache, storeId: string): PendingPublish {
  return cache.value[storeId] ?? EMPTY
}

/** SSR: write pending-publish into the shared cache so the publish dot is in the HTML. */
export async function fillPendingPublishCache(
  cache: PendingPublishCache,
  apiFetch: <T>(path: string) => Promise<T>,
  storeId: string,
): Promise<void> {
  if (!storeId) return
  try {
    const res = await apiFetch<{ data: PendingPublish }>(
      `/online-stores/${encodeURIComponent(storeId)}/pending-publish`,
    )
    cache.value = { ...cache.value, [storeId]: res.data ?? EMPTY }
  }
  catch {
    // 401/403 — leave the cache alone so the HTML stays without the dot.
  }
}

/** Light the publish dot right after a studio save. */
export function markStoreDirty(
  cache: PendingPublishCache,
  storeId: string,
  group: PendingPublishGroupKey = 'design',
): void {
  const current = pendingPublishFor(cache, storeId)
  const groups = current.groups.some(row => row.key === group)
    ? current.groups.map(row =>
        row.key === group
          ? { ...row, count: row.count + 1, updated: row.updated + 1 }
          : row,
      )
    : [...current.groups, { key: group, count: 1, created: 0, updated: 1, deleted: 0 }]

  cache.value = {
    ...cache.value,
    [storeId]: { ...current, has_pending: true, count: current.count + 1, groups },
  }
}

/** Clear the dot after the draft went live, or was thrown away. */
export function markStoreClean(
  cache: PendingPublishCache,
  storeId: string,
  options: { published?: boolean } = {},
): void {
  const current = pendingPublishFor(cache, storeId)

  cache.value = {
    ...cache.value,
    [storeId]: {
      ...current,
      has_pending: false,
      count: 0,
      groups: [],
      published: options.published ?? current.published,
      last_published_at: options.published
        ? new Date().toISOString()
        : current.last_published_at,
    },
  }
}

/**
 * Unpublished changes waiting in one storefront's draft.
 *
 * The design studio reads this to decide whether the publish button is active
 * and whether to show its pending dot.
 */
export function usePendingPublish(storeId: MaybeRefOrGetter<string | null | undefined>) {
  const { apiFetch } = useApi()
  const cache = usePendingPublishCache()
  const loading = useState('store-pending-publish-loading', () => false)

  const id = computed(() => {
    const value = toValue(storeId)
    return value ? String(value) : ''
  })

  const state = computed<PendingPublish>(() =>
    id.value ? pendingPublishFor(cache, id.value) : EMPTY,
  )

  async function refresh(): Promise<void> {
    if (!id.value) return
    const key = id.value
    loading.value = true
    try {
      const res = await apiFetch<{ data: PendingPublish }>(
        `/online-stores/${encodeURIComponent(key)}/pending-publish`,
      )
      cache.value = { ...cache.value, [key]: res.data ?? EMPTY }
    }
    catch {
      // A failed poll must not blank out a dot the owner is looking at.
    }
    finally {
      loading.value = false
    }
  }

  async function listDrafts(options: {
    group?: string
    cursor?: string | null
    perPage?: number
  } = {}): Promise<StoreDraftPage> {
    if (!id.value) return { drafts: [], nextCursor: null }

    const params = new URLSearchParams()
    if (options.group) params.set('group', options.group)
    params.set('per_page', String(options.perPage ?? STORE_DRAFTS_PER_PAGE))
    if (options.cursor) params.set('cursor', options.cursor)
    const qs = params.toString()
    const path = `/online-stores/${encodeURIComponent(id.value)}/drafts${qs ? `?${qs}` : ''}`

    const res = await apiFetch<{
      data: StoreDraft[]
      meta?: { next_cursor?: string | null }
      links?: { next?: string | null }
    }>(path)

    let nextCursor: string | null = res.meta?.next_cursor ?? null
    if (!nextCursor && res.links?.next) {
      try {
        const url = new URL(res.links.next, 'https://local.invalid')
        nextCursor = url.searchParams.get('cursor')
      }
      catch {
        nextCursor = null
      }
    }

    return {
      drafts: (res.data ?? []).map(draft => ({
        ...draft,
        changed_fields: draft.changed_fields ?? [],
        changes: draft.changes ?? [],
      })),
      nextCursor,
    }
  }

  return {
    loading,
    state,
    hasPending: computed(() => state.value.has_pending),
    count: computed(() => state.value.count),
    groups: computed(() => state.value.groups),
    published: computed(() => state.value.published),
    lastPublishedAt: computed(() => state.value.last_published_at),
    refresh,
    listDrafts,
    markDirty: (group?: PendingPublishGroupKey) => id.value && markStoreDirty(cache, id.value, group),
    markPublished: () => id.value && markStoreClean(cache, id.value, { published: true }),
    markDiscarded: () => id.value && markStoreClean(cache, id.value),
  }
}
